import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { RotateCcw, DoorOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/AppShell";
import { MoveStatusToggle } from "@/components/MoveStatusToggle";
import { api } from "@/lib/api";
import { NONE, useLocations, useErrorToast } from "@/lib/hooks";
import { useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

const Progress = ({ done, packed, total, label, testId }) => (
  <div className="space-y-1.5" data-testid={testId}>
    <div className="flex justify-between gap-2 text-sm"><span className="font-semibold">{label}</span><span className="font-mono text-slate-500">{done}/{total}</span></div>
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className="bg-emerald-500 transition-[width] duration-500" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
      <div className="bg-amber-400 transition-[width] duration-500" style={{ width: `${total ? (packed / total) * 100 : 0}%` }} />
    </div>
  </div>
);

const roomOf = (box) => {
  const ancestors = box.path.slice(0, -1);
  return [...ancestors].reverse().find((p) => p.kind === "room") || ancestors[ancestors.length - 1] || null;
};

export default function MovePage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { canWrite } = useWs();
  const showErr = useErrorToast();
  const { data: locs = NONE, isLoading } = useLocations();
  const rooms = useMemo(() => {
    const map = new Map();
    locs.filter((l) => l.kind === "box").forEach((b) => {
      const room = roomOf(b);
      const key = room?.id || "none";
      if (!map.has(key)) map.set(key, { room, boxes: [] });
      map.get(key).boxes.push(b);
    });
    return [...map.values()];
  }, [locs]);
  const boxes = rooms.flatMap((r) => r.boxes);
  const count = (list, s) => list.filter((b) => b.move_status === s).length;
  const reset = async () => {
    if (!window.confirm(`${t("move.reset")} ?`)) return;
    try { await api.post("/move/reset"); qc.invalidateQueries({ queryKey: ["locations"] }); } catch (e) { showErr(e); }
  };

  return (
    <div className="space-y-8">
      <PageHeader title={t("move.title")} subtitle={t("move.subtitle")} testId="move-title"
        actions={canWrite && boxes.length > 0 && <Button data-testid="move-reset-button" variant="outline" className="h-11 gap-2" onClick={reset}><RotateCcw className="w-4 h-4" />{t("move.reset")}</Button>} />
      {isLoading ? <p className="text-slate-500">{t("common.loading")}</p> : !boxes.length ? (
        <p data-testid="move-empty" className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500">{t("move.empty")}</p>
      ) : (
        <>
          <section className="rounded-2xl bg-slate-900 p-5 sm:p-6 text-white space-y-3" data-testid="move-overall">
            <Progress label={t("move.overall")} done={count(boxes, "unpacked")} packed={count(boxes, "packed")} total={boxes.length} testId="move-overall-progress" />
            <p className="text-sm text-slate-300">{t("move.progress", { done: count(boxes, "unpacked"), total: boxes.length })} · {t("move.packedCount", { n: count(boxes, "packed") })}</p>
          </section>
          <div className="grid lg:grid-cols-2 gap-6">
            {rooms.map(({ room, boxes: list }) => (
              <section key={room?.id || "none"} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid={`move-room-${room?.id || "none"}`}>
                <div className="flex items-center gap-2"><DoorOpen className="w-5 h-5 text-blue-600" /><h2 className="text-lg font-bold">{room?.name || t("move.noRoom")}</h2></div>
                <Progress label={t("move.unpacked")} done={count(list, "unpacked")} packed={count(list, "packed")} total={list.length} testId={`move-room-progress-${room?.id || "none"}`} />
                <ul className="space-y-3">
                  {list.map((b) => (
                    <li key={b.id} className="space-y-2 rounded-xl border border-slate-100 p-3">
                      <Link to={`/locations/${b.id}`} className="flex justify-between gap-2 font-semibold hover:underline" data-testid={`move-box-${b.id}`}>
                        <span className="truncate">{b.name}</span><span className="font-mono text-xs text-slate-400 shrink-0">{t("loc.itemsCount", { count: b.item_count })}</span>
                      </Link>
                      <MoveStatusToggle loc={b} compact />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
